import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { fromEvent, merge, of, timer } from 'rxjs';
import {
    distinctUntilChanged,
    map,
    shareReplay,
    startWith,
    switchMap,
} from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class TabIdleService {
    private readonly idleTime = 10 * 60 * 1000;
    private document = inject(DOCUMENT);
    private destroyRef = inject(DestroyRef);
    private zone = inject(NgZone);

    private documentHidden = fromEvent(this.document, 'visibilitychange').pipe(
        startWith(false),
        map(() => this.document.hidden),
        shareReplay(1)
    );

    /**
     * An observable that emits true when the tab is in the background for idleTime milliseconds.
     * @param idleTime The time in milliseconds to wait before declaring the tab idle. Default is 10 minutes.
     */
    getIdle(idleTime: number = this.idleTime) {
        return this.zone.runOutsideAngular(() =>
            this.documentHidden.pipe(
                switchMap(hidden => {
                    // 'idle' parameter from documentHidden indicates if the tab is currently hidden
                    if (!hidden) {
                        // If the tab is active (not hidden)
                        // Emit false immediately. No timer is needed as the tab is active.
                        // This helps prevent long-running timers that can affect initial app stability (e.g., hydration NG0506).
                        return of(false);
                    } else {
                        // If the tab is hidden
                        // The tab is hidden, so we start the process to declare it idle after idleTime.
                        // Emit false immediately (signifying "not yet confirmed idle" or "just became hidden").
                        // Then, after idleTime, emit true if the tab has remained hidden (timer wasn't cancelled by switchMap).
                        // The timer only runs when the tab is actually hidden.
                        return merge(
                            of(false),
                            timer(idleTime).pipe(map(() => true)) // Map to true because 'idle' (isTabHidden) is true
                        );
                    }
                }),
                distinctUntilChanged(),
                shareReplay(1),
                takeUntilDestroyed(this.destroyRef)
            )
        );
    }

    /*
     * This signal will emit true when the tab is in the background for 10 minutes.
     */
    isIdle = toSignal(this.getIdle(this.idleTime));
    constructor() {}
}
